import { Deployment, DeploymentStatus, DeploymentStatusError, TaskStatus } from '@back4app2/sdk';
import { useEffect, useState } from 'react';
import back4app2 from '../back4app2';
import { DsButton, ModalDialog } from "./ds";
import { ReactComponent as TriangleExclamationIcon } from '../assets/images/triangle-exclamation.svg';

interface CancelBuildProps {
  deployment: Deployment;
}

const inProgressDeploymentStatus = [DeploymentStatus.INITIALIZING, DeploymentStatus.INITIALIZED, DeploymentStatus.DEPLOYING, DeploymentStatus.QUEUED];

/**
 * Whether this deployment is still stoppable — the same test the button makes
 * of itself, exported so a caller can decide what to draw in its place. The
 * Overview's log header shows Cancel here and Open logs the rest of the time,
 * and it has to know which without rendering both.
 */
export const isBuildCancelable = (deployment?: Deployment) => !!deployment
  && inProgressDeploymentStatus.includes(deployment.status)
  && (!deployment.deployTask || [TaskStatus.WAITING, TaskStatus.DOING].includes(deployment.deployTask.status));

const CancelBuildButton = ({ deployment } : CancelBuildProps) => {
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    return () => {
      setOpenModal(false);
      setErrorMessage('');
    }
  }, []);

  const openCancelBuildModal = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setOpenModal(true);
  }

  const onCancelBuild = async (deployment: Deployment) => {
    setErrorMessage('');
    setIsLoading(true);
    try {
      await back4app2.cancelDeploymentBuild(deployment.id);
      setOpenModal(false);
    } catch (err) {
      if (err instanceof DeploymentStatusError) {
        setErrorMessage('Sorry, the build cannot be canceled!');
      } else {
        console.error('error in cancelling the build', err);
        setErrorMessage('Sorry, something went wrong while canceling build!');
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    isBuildCancelable(deployment) ?
    <>
      <button
        className="b4-focus h-[30px] px-3 rounded-control border border-state-err/60 bg-transparent text-state-err text-ds-125 font-semibold transition-colors duration-[120ms] hover:bg-state-err/10 hover:border-state-err"
        onClick={openCancelBuildModal}
      >
        Cancel
      </button>
      {/* `hidden` while closed so this stops being a flex item: in a
          `justify-between` row it counted as a third child and shoved the
          button into the middle of the card. */}
      <div className={openModal ? '' : 'hidden'} onClick={e => e.stopPropagation()}>
        <ModalDialog
          open={openModal}
          onClose={() => setOpenModal(false)}
          icon={<TriangleExclamationIcon className="text-state-err" width="20px" height="20px" />}
          title="Cancel this build?"
          blurb="The deployment stops where it is. The app keeps running whatever it was running before."
          footer={errorMessage ? (
            <DsButton onClick={() => setOpenModal(false)}>Close</DsButton>
          ) : (<>
            <DsButton onClick={() => setOpenModal(false)} isDisabled={isLoading}>Keep building</DsButton>
            <DsButton
              variant="danger"
              isDisabled={isLoading}
              onClick={isLoading ? undefined : () => onCancelBuild(deployment)}
            >
              {isLoading ? 'Canceling…' : 'Cancel build'}
            </DsButton>
          </>)}
        >
          {errorMessage
            ? <span className="text-ds-115 text-state-err">{errorMessage}</span>
            : <span className="font-courrier text-ds-115 text-navy-300 break-all">{deployment.id}</span>}
        </ModalDialog>
      </div>
    </> : null
  )
}

export default CancelBuildButton;